import { useState, useMemo, useEffect } from "react";
import { ContentContainer } from "components/Container";
import { getPatterns, PATTERNS_PER_PAGE } from "data/patterns";
import { PatternCard } from "./PatternCard";
import { ArrowIcon } from "./ArrowIcon";
import { Direction } from "types";
import { useTranslation } from "react-i18next";

const CARD_BACKGROUND_COLORS = [
  "#FFC460",
  "#FFA6BA",
  "#7DD6D5",
  "#6BCAFF",
  "#8CD6AD",
  "#6BCAFF",
  "#FFC460",
  "#FFA6BA",
];

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const PatternCards = () => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [lang, setLang] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChange = (newLang: string) => setLang(newLang);
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const patterns = useMemo(() => getPatterns(lang), [lang]);
  const shuffledPatterns = useMemo(() => shuffleArray(patterns), [patterns]);

  const totalPages = Math.ceil(shuffledPatterns.length / PATTERNS_PER_PAGE);
  const startIndex = currentPage * PATTERNS_PER_PAGE;
  const visiblePatterns = shuffledPatterns.slice(
    startIndex,
    startIndex + PATTERNS_PER_PAGE
  );

  const goToPrev = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const goToNext = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  return (
    <section id="patterns" className="mt-20 lg:mt-36 scroll-mt-10">
      <ContentContainer>
        <h2 className="max-w-78 md:max-w-full title-text-style">
          {t("patterns.title")}
        </h2>
        <p className="subtitle-text-style max-w-812px mt-5 lg:mt-6">
          {t("patterns.description")}
        </p>

        <div className="relative">
          <div className="pattern-block">
            {visiblePatterns.map((pattern, index) => (
              <PatternCard
                key={pattern.id}
                pattern={pattern}
                backgroundColor={CARD_BACKGROUND_COLORS[index % CARD_BACKGROUND_COLORS.length]}
              />
            ))}
          </div>

          {/* Navigation arrows */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={goToPrev}
                className="p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow border border-blueBorder"
                aria-label={t("patterns.previous")}
              >
                <ArrowIcon direction={Direction.Left} className="text-textPrimary" />
              </button>

              <div className="flex gap-2">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentPage
                        ? "bg-primary"
                        : "bg-textSecondaryGrey hover:bg-textPrimary"
                    }`}
                    aria-label={`${t("patterns.page")} ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                className="p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow border border-blueBorder"
                aria-label={t("patterns.next")}
              >
                <ArrowIcon direction={Direction.Right} className="text-textPrimary" />
              </button>
            </div>
          )}
        </div>
      </ContentContainer>
    </section>
  );
};
